import { Decoration, type DecorationSet, type EditorState, EditorView, type Extension, type Range, StateEffect, type StateEffectType, StateField } from "@uiw/react-codemirror";
import { EDITOR_SURFACE } from "../../common/editor-theme";
import type { LineMark } from "./myers";

// Dispatching rather than replacing the state keeps a swap undoable and runs the change handler like any edit.
export function replaceDoc(view: EditorView | null, text: string) {
  view?.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text } });
}

// Red for what the left document has and the right does not, green the other way, and the word tints over both.
const DIFF_THEME = EditorView.theme({
  // Qualified with `.cm-line` so the diff tint beats the active line's whatever order the two themes are injected in.
  ".cm-line.cm-diff-removed": { backgroundColor: "rgba(224, 49, 49, 0.16)" },
  ".cm-line.cm-diff-added": { backgroundColor: "rgba(47, 158, 68, 0.18)" },
  ".cm-diff-removed-text": { backgroundColor: "rgba(224, 49, 49, 0.4)" },
  ".cm-diff-added-text": { backgroundColor: "rgba(47, 158, 68, 0.45)" },
});

const BASE_EXTENSIONS = [EditorView.lineWrapping, ...EDITOR_SURFACE, DIFF_THEME];

export function paneExtensions(marks: Extension, language: Extension | null): Extension[] {
  return language ? [...BASE_EXTENSIONS, marks, language] : [...BASE_EXTENSIONS, marks];
}

export interface DiffMarks {
  extension: Extension;
  set: StateEffectType<LineMark[]>;
}

// One per side, at module scope: the effect and the field have to be the same objects the running editor was given.
export function diffMarks(lineClass: string, textClass: string): DiffMarks {
  const set = StateEffect.define<LineMark[]>();
  const lineDecoration = Decoration.line({ class: lineClass });
  const textDecoration = Decoration.mark({ class: textClass });

  const field = StateField.define<DecorationSet>({
    create: () => Decoration.none,
    update(marks, tr) {
      for (const effect of tr.effects) {
        if (effect.is(set)) return buildDecorations(tr.state, effect.value, lineDecoration, textDecoration);
      }
      // Marks answer for the text they were read from, so an edit drops them rather than letting them slide along.
      return tr.docChanged ? Decoration.none : marks;
    },
    provide: (field) => EditorView.decorations.from(field),
  });

  return { extension: field, set };
}

function buildDecorations(
  state: EditorState,
  marks: LineMark[],
  lineDecoration: Decoration,
  textDecoration: Decoration,
): DecorationSet {
  const ranges: Range<Decoration>[] = [];

  for (const mark of marks) {
    if (mark.line < 1 || mark.line > state.doc.lines) continue;
    const line = state.doc.line(mark.line);
    ranges.push(lineDecoration.range(line.from));

    for (const span of mark.spans) {
      const from = line.from + span.from;
      const to = Math.min(line.from + span.to, line.to);
      if (to > from) ranges.push(textDecoration.range(from, to));
    }
  }

  // Line decorations sort ahead of the marks that start at the same offset, so one sorted set holds both.
  return Decoration.set(ranges, true);
}

export const LEFT_MARKS = diffMarks("cm-diff-removed", "cm-diff-removed-text");
export const RIGHT_MARKS = diffMarks("cm-diff-added", "cm-diff-added-text");

export const NO_MARKS: LineMark[] = [];
